import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, addDoc } from 'firebase/firestore';

/**
 * Lógica do Cron Job para Notificação de Usuários "Esquecidos"
 * 
 * Esta função foi projetada para ser executada diariamente (idealmente às 00:01 horário de Brasília).
 * Ela identifica eventos (Jogos/Corridas) que ocorrerão "Amanhã" (entre 24h e 48h a partir de agora),
 * verifica quais participantes dos bolões correspondentes não palpitaram, e envia um lembrete.
 * 
 * Como usar:
 * Pode ser importada e chamada em um botão de "Teste de Cron" no Painel Admin,
 * ou adaptada para rodar em um ambiente Node.js (Firebase Cloud Functions).
 */

export const runDailyReminder = async () => {
    console.log("--- Iniciando Rotina de Notificação Diária ---");

    // 1. Definição do intervalo de tempo (0h a 48h a partir de "agora")
    // "Hoje" e "Amanhã" para quem roda à 00:01
    const now = new Date();

    // Ajuste: Define estritamente "Hoje" e "Amanhã" no fuso horário de Brasília (UTC-3)
    // 1. Obter componentes da data atual em Brasília
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    const parts = formatter.formatToParts(now);

    // Extrair ano, mês e dia
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;

    // 2. Data base simulada BRT (meio dia para evitar bugs de virada)
    const todayBRT = new Date(`${year}-${month}-${day}T12:00:00-03:00`);
    const tomorrowBRT = new Date(todayBRT);
    tomorrowBRT.setDate(tomorrowBRT.getDate() + 1);

    // 3. Obter componentes de Hoje e Amanhã para limites
    // Start Window = Hoje 00:00:00
    const startWindow = new Date(`${year}-${month}-${day}T00:00:00.000-03:00`);

    // Obter componentes de Amanhã
    const tParts = formatter.formatToParts(tomorrowBRT);
    const tYear = tParts.find(p => p.type === 'year').value;
    const tMonth = tParts.find(p => p.type === 'month').value;
    const tDay = tParts.find(p => p.type === 'day').value;

    // End Window = Amanhã 23:59:59
    const endWindow = new Date(`${tYear}-${tMonth}-${tDay}T23:59:59.999-03:00`);

    console.log(`Buscando eventos entre: ${startWindow.toISOString()} e ${endWindow.toISOString()}`);

    try {
        // 2. Buscar eventos (Matches) no intervalo
        const matchesRef = collection(db, "matches");
        // O Firestore requer índices compostos para query de intervalo. 
        // Se der erro, use o link no console do firebase para criar o índice.
        const qMatches = query(
            matchesRef,
            where("matchDate", ">=", startWindow),
            where("matchDate", "<=", endWindow)
        );

        const matchesSnapshot = await getDocs(qMatches);

        if (matchesSnapshot.empty) {
            console.log("Nenhum evento encontrado para o período.");
            return;
        }

        const upcomingMatches = [];
        const champIdsSet = new Set();

        matchesSnapshot.forEach(doc => {
            const data = doc.data();
            upcomingMatches.push({ id: doc.id, ...data });
            if (data.championshipId) champIdsSet.add(data.championshipId);
        });

        console.log(`${upcomingMatches.length} eventos encontrados em ${champIdsSet.size} campeonatos.`);

        // 3. Buscar Bolões ativos vinculados a esses campeonatos
        const poolsToCheck = [];
        const poolsRef = collection(db, "pools");
        // Atenção: 'in' suporta no máximo 10 valores. Se houver muitos campeonatos, precisaria dividir.
        if (champIdsSet.size > 0) {
            const qPools = query(poolsRef, where("championshipId", "in", Array.from(champIdsSet)));
            const poolsSnapshot = await getDocs(qPools);
            poolsSnapshot.forEach(doc => {
                poolsToCheck.push({ id: doc.id, ...doc.data() });
            });
        }

        // 4. Identificar usuários pendentes
        // Estrutura: { userId: { userDisplayName: string, userEmail: string, missingMatches: [matchDetails] } }
        const pendingNotifications = {};

        for (const pool of poolsToCheck) {
            // Filtra jogos deste bolão (com base no campeonato)
            const poolMatches = upcomingMatches.filter(m => m.championshipId === pool.championshipId);

            if (poolMatches.length === 0) continue;

            // Itera sobre participantes ativos
            const activeParticipants = (pool.participants || []).filter(p => p.status === 'active');

            for (const participant of activeParticipants) {
                // Busca os palpites deste usuário neste bolão
                const userBetsRef = doc(db, "pools", pool.id, "bets", participant.uid);
                const userBetsSnap = await getDoc(userBetsRef);
                const userBetsData = userBetsSnap.exists() ? userBetsSnap.data() : {};

                poolMatches.forEach(match => {
                    // Verifica se TEM palpite.
                    // Para futebol: verifica se existe o objeto do matchId
                    // Para F1: verifica se p1, p2, p3 estão preenchidos
                    const hasBet = userBetsData[match.id] && (
                        match.type === 'f1_race'
                            ? (userBetsData[match.id].p1 && userBetsData[match.id].p2 && userBetsData[match.id].p3)
                            : (userBetsData[match.id].betA !== undefined && userBetsData[match.id].betB !== undefined)
                    );

                    if (!hasBet) {
                        // Adiciona à lista de pendências
                        if (!pendingNotifications[participant.uid]) {
                            pendingNotifications[participant.uid] = {
                                uid: participant.uid,
                                name: participant.displayName,
                                matches: []
                            };
                        }

                        // Verifica se este jogo já foi listado para este usuário (ex: mesmo jogo em outro bolão)
                        const existingMatch = pendingNotifications[participant.uid].matches.find(m => m.id === match.id);

                        if (existingMatch) {
                            // Se já existe, apenas adiciona o nome do bolão atual à lista (se não estiver lá)
                            if (pool.name && !existingMatch.poolNames.includes(pool.name)) {
                                existingMatch.poolNames.push(pool.name);
                            }
                        } else {
                            // Se não existe, cria o registro
                            pendingNotifications[participant.uid].matches.push({
                                id: match.id,
                                teamA: match.teamA,
                                teamB: match.teamB, // Pode ser undefined para F1
                                country: match.country, // F1
                                type: match.type || 'football',
                                date: match.matchDate.toDate(),
                                poolNames: pool.name ? [pool.name] : []
                            });
                        }
                    }
                });
            }
        }

        // 5. Enviar Emails
        const usersToNotify = Object.values(pendingNotifications);
        console.log(`Encontrados ${usersToNotify.length} usuários com palpites pendentes.`);

        for (const user of usersToNotify) {
            // Busca o email real do usuário na coleção 'users'
            let email = user.email;
            if (!email) {
                try {
                    const userDocRef = doc(db, "users", user.uid);
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                        email = userDocSnap.data().email;
                    }
                } catch (err) {
                    console.error(`Erro ao buscar email para uid ${user.uid}:`, err);
                }
            }

            if (!email) {
                console.warn(`Email não encontrado para usuário ${user.name} (${user.uid}).`);
                continue; // Pula este usuário se não tiver email
            }

            await sendEmailToFirestore(user.name, email, user.matches);
        }

        console.log("--- Rotina Finalizada com Sucesso ---");

    } catch (error) {
        console.error("Erro na rotina de notificação:", error);
    }
};

/**
 * Envia o email gravando um documento na coleção 'mail' (Trigger Email Extension)
 */
const sendEmailToFirestore = async (userName, userEmail, matches) => {
    const today = new Date();
    // Normaliza 'hoje' para comparação de dia apenas
    const isToday = (date) => {
        const d = new Date(date);
        const t = new Date();
        return d.getDate() === t.getDate() &&
            d.getMonth() === t.getMonth() &&
            d.getFullYear() === t.getFullYear();
    };

    const matchesToday = matches.filter(m => isToday(m.date));
    const matchesTomorrow = matches.filter(m => !isToday(m.date));

    // Formata nomes dos bolões
    const formatPools = (pools) => {
        if (!pools || pools.length === 0) return '';
        return ` <span style="font-size: 0.85em; color: #666;">(Bolão: ${pools.join(', ')})</span>`;
    };

    const formatPoolsText = (pools) => {
        if (!pools || pools.length === 0) return '';
        return ` (Bolão: ${pools.join(', ')})`;
    };

    // Gera HTML para lista de jogos
    const generateHtmlList = (list) => {
        return list.map(m => {
            const timeStr = m.date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
            if (m.type === 'f1_race') {
                return `<li>🏎️ <strong>GP de ${m.country}</strong> - ${timeStr}${formatPools(m.poolNames)}</li>`;
            } else {
                return `<li>⚽ <strong>${m.teamA} x ${m.teamB}</strong> - ${timeStr}${formatPools(m.poolNames)}</li>`;
            }
        }).join('');
    };

    // Gera Texto para lista de jogos
    const generateTextList = (list) => {
        return list.map(m => {
            const timeStr = m.date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
            if (m.type === 'f1_race') {
                return `🏎️ GP de ${m.country} - ${timeStr}${formatPoolsText(m.poolNames)}`;
            } else {
                return `⚽ ${m.teamA} x ${m.teamB} - ${timeStr}${formatPoolsText(m.poolNames)}`;
            }
        }).join('\n');
    };

    const todayHtml = matchesToday.length > 0
        ? `<h3>🚨 É HOJE! Última chamada:</h3><ul>${generateHtmlList(matchesToday)}</ul>`
        : '';

    const tomorrowHtml = matchesTomorrow.length > 0
        ? `<h3>📅 Amanhã:</h3><ul>${generateHtmlList(matchesTomorrow)}</ul>`
        : '';

    const todayText = matchesToday.length > 0
        ? `🚨 É HOJE! Última chamada:\n${generateTextList(matchesToday)}\n`
        : '';

    const tomorrowText = matchesTomorrow.length > 0
        ? `📅 Amanhã:\n${generateTextList(matchesTomorrow)}\n`
        : '';

    const subject = `⚠️ Palpites Pendentes: Não esqueça de palpitar!`;

    const html = `
    <div style="font-family: Arial, sans-serif; color: #333;">
        <h2>Olá, ${userName}!</h2>
        <p>Você ainda tem palpites pendentes. Não deixe para a última hora!</p>
        
        ${todayHtml}
        ${tomorrowHtml}

        <p>
            <a href="https://boloestop.com.br/" style="background-color: #0d9488; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; font-weight: bold;">
                PALPITAR AGORA
            </a>
        </p>
        <p>Boa sorte!<br>Equipe do Bolão</p>
    </div>
    `;

    const text = `
    Olá, ${userName}!
    
    Você ainda tem palpites pendentes. Não deixe para a última hora!
    
    ${todayText}
    ${tomorrowText}
    
    Acesse o app para palpitar: https://boloestop.com.br/
    
    Boa sorte!
    Equipe do Bolão
    `;

    try {
        await addDoc(collection(db, "mail"), {
            to: [userEmail],
            from: "Bolões TOP <lembrete@boloestop.com.br>",
            message: {
                subject: subject,
                text: text,
                html: html
            }
        });
        console.log(`[EMAIL] Enviado para ${userEmail} via Firestore.`);
    } catch (error) {
        console.error(`[EMAIL] Erro ao gravar email para ${userEmail}:`, error);
    }
};

/**
 * Envia um email simulado com dados fictícios para validar o Layout/Texto.
 */
export const sendMockDailyReminder = async (targetEmail) => {
    if (!targetEmail) return;

    const mockMatches = [
        {
            id: 'mockToday1',
            type: 'football',
            teamA: 'Flamengo',
            teamB: 'Vasco',
            date: new Date(), // Hoje
            poolNames: ['Cariocão 2026']
        },
        {
            id: 'mockTomorrow1',
            type: 'football',
            teamA: 'Brasil',
            teamB: 'Argentina',
            date: new Date(new Date().setHours(new Date().getHours() + 24)), // Amanhã
            poolNames: ['Copa do Mundo 2026', 'Bolão da Firma']
        },
        {
            id: 'mockTomorrow2',
            type: 'f1_race',
            country: 'Mônaco',
            date: new Date(new Date().setHours(new Date().getHours() + 26)), // Amanhã + 2h
            poolNames: ['Temporada F1 2026']
        }
    ];

    console.log(`Enviando Mock de Layout para ${targetEmail}...`);
    await sendEmailToFirestore("Admin Teste", targetEmail, mockMatches);
    alert(`Email de Simulação enviado para ${targetEmail}. Verifique o texto.`);
};
